import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import Customer from "views/pages/Store/Customers/Customer";
import PopupCustomer from "views/pages/Store/Customers/PopupCustomer";
import PopupOrders from "views/pages/Store/Customers/PopupOrders";
import PopupPool from "views/pages/Store/TicketRestaurant/PopupPool";
import PopupAdvantage from "views/pages/Store/TicketRestaurant/PopupAdvantage";
import { customersOperations } from "state/ducks/store/customers";
import {
  ticketRestaurantOperations,
  ticketRestaurantSelector,
} from "state/ducks/store/ticketRestaurant";
import { notificationsOperations } from "state/ducks/notifications";

/**
 * Fiche client complète de l'onglet intendance : réutilise le panneau client
 * de la page gestion client (`Customers/Customer.js`) et ses popups
 * (commandes, porte-monnaie, avantages, édition).
 *
 * La liste complète des clients n'étant pas chargée sur la page SAV, le détail
 * du client est récupéré à l'ouverture par `getStoreCustomerDetail`.
 */
const CustomerDetailModal = (props) => {
  const { customerId, onClose } = props;

  const dispatch = useDispatch();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);

  const [isOrdersOpen, setIsOrdersOpen] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);

  const [isPoolOpen, setIsPoolOpen] = useState(false);
  const [isPoolLoading, setIsPoolLoading] = useState(false);

  const [isAdvantageOpen, setIsAdvantageOpen] = useState(false);
  const [isAdvantageLoading, setIsAdvantageLoading] = useState(false);

  const customer = useSelector(
    (state) => state.store.customers.customer,
    shallowEqual
  );

  // Liste légère des clients (page SAV) : suffit aux contrôles de doublon
  // email/téléphone de la popup d'édition
  const savCustomers = useSelector(
    (state) => state.store.sav.admin.customers,
    shallowEqual
  );

  const customerPool = useSelector((state) =>
    ticketRestaurantSelector.getCustomerPoolSelector(state)
  );

  const customerAdvantage = useSelector((state) =>
    ticketRestaurantSelector.getCustomerAdvantageSelector(state)
  );

  // Charge (ou recharge) le détail à l'ouverture et à chaque changement de client
  useEffect(() => {
    if (!customerId) {
      return;
    }
    dispatch(customersOperations.getStoreCustomerDetail(customerId)).catch(() => {
      dispatch(
        notificationsOperations.getResponseError(
          "Impossible de charger la fiche de ce client"
        )
      );
    });
  }, [customerId, dispatch]);

  // Referme les popups filles quand la fiche se ferme
  useEffect(() => {
    if (!customerId) {
      setIsEditOpen(false);
      setIsOrdersOpen(false);
      setIsPoolOpen(false);
      setIsAdvantageOpen(false);
    }
  }, [customerId]);

  const openOrders = useCallback(
    (theCustomer, e) => {
      e.preventDefault();
      setIsOrdersOpen(true);
      setIsOrdersLoading(true);
      dispatch(customersOperations.getStoreCustomerOrders(theCustomer)).then(
        () => setIsOrdersLoading(false),
        () => {
          setIsOrdersOpen(false);
          setIsOrdersLoading(false);
        }
      );
    },
    [dispatch]
  );

  const openPool = useCallback(
    (theCustomerId, e) => {
      e.preventDefault();
      setIsPoolOpen(true);
      setIsPoolLoading(true);
      dispatch(ticketRestaurantOperations.getCustomerPool(theCustomerId)).then(
        () => setIsPoolLoading(false),
        () => {
          setIsPoolOpen(false);
          setIsPoolLoading(false);
        }
      );
    },
    [dispatch]
  );

  const openAdvantage = useCallback(
    (theCustomerId, e) => {
      e.preventDefault();
      setIsAdvantageOpen(true);
      setIsAdvantageLoading(true);
      dispatch(
        ticketRestaurantOperations.getCustomerAdvantage(theCustomerId)
      ).then(
        () => setIsAdvantageLoading(false),
        () => {
          setIsAdvantageOpen(false);
          setIsAdvantageLoading(false);
        }
      );
    },
    [dispatch]
  );

  const customerHandle = (mode, data) => {
    if (mode !== "edit") {
      return;
    }
    setIsEditLoading(true);
    dispatch(customersOperations.updateStoreCustomer(data)).then(
      () => {
        setIsEditLoading(false);
        setIsEditOpen(false);
        dispatch(customersOperations.getStoreCustomerDetail(customerId));
      },
      () => {
        setIsEditLoading(false);
      }
    );
  };

  const sendSMSHandle = (theCustomer) => {
    return dispatch(customersOperations.sendCodeBySMS(theCustomer)).then(
      (result) => {
        return dispatch(
          notificationsOperations.getResponseSuccess(
            result?.data?.result?.success ? "SMS envoyé" : "Echec de l'envoi"
          )
        );
      }
    );
  };

  return (
    <>
      <Modal show={!!customerId} onHide={onClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Fiche client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Pas d'`establishment` : évite le rechargement de toute la base
              clients après un changement d'option (cf. Customers/Options/ToDelete) */}
          <Customer
            customer={customer}
            openPopupCustomerHandle={() => setIsEditOpen(true)}
            sendSMSHandle={sendSMSHandle}
            onOpenOrders={openOrders}
            onOpenPool={openPool}
            onOpenAdvantage={openAdvantage}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      <PopupCustomer
        show={isEditOpen}
        onHide={() => setIsEditOpen(false)}
        onEscapeKeyDown={() => setIsEditOpen(false)}
        customerHandle={customerHandle}
        isLoading={isEditLoading}
        customer={customer}
        isEdit
        customers={savCustomers || []}
      />

      <PopupOrders
        show={isOrdersOpen}
        onHide={() => setIsOrdersOpen(false)}
        onEscapeKeyDown={() => setIsOrdersOpen(false)}
        customer={customer}
        isLoading={isOrdersLoading}
      />

      <PopupPool
        show={isPoolOpen}
        onHide={() => setIsPoolOpen(false)}
        onEscapeKeyDown={() => setIsPoolOpen(false)}
        customerPool={customerPool}
        isLoading={isPoolLoading}
      />

      <PopupAdvantage
        show={isAdvantageOpen}
        onHide={() => setIsAdvantageOpen(false)}
        onEscapeKeyDown={() => setIsAdvantageOpen(false)}
        customerAdvantage={customerAdvantage}
        isLoading={isAdvantageLoading}
        customerId={customerId}
      />
    </>
  );
};

CustomerDetailModal.propTypes = {};

CustomerDetailModal.defaultProps = {};

export default CustomerDetailModal;
